import {
  CompactSelection,
  type GridSelection,
  type Item,
} from "@glideapps/glide-data-grid";

export type TabNavigationTrigger =
  | {
      mode: "selection";
      key: string;
      altKey: boolean;
      ctrlKey: boolean;
      metaKey: boolean;
      shiftKey: boolean;
    }
  | { mode: "editing"; movement: Item };

const isForwardTabTrigger = (trigger: TabNavigationTrigger) => {
  if (trigger.mode === "editing") {
    return trigger.movement[0] === 1 && trigger.movement[1] === 0;
  }

  return (
    trigger.key === "Tab" &&
    !trigger.altKey &&
    !trigger.ctrlKey &&
    !trigger.metaKey &&
    !trigger.shiftKey
  );
};

const getTabNavigationTarget = ({
  columnCount,
  rowCount,
  selection,
  trigger,
}: {
  columnCount: number;
  rowCount: number;
  selection: GridSelection;
  trigger: TabNavigationTrigger;
}): Item | null => {
  if (!isForwardTabTrigger(trigger)) return null;

  const currentCell = selection.current?.cell;
  if (!currentCell) return null;

  const nextColumn = currentCell[0] + 1;
  if (nextColumn < columnCount) {
    return [nextColumn, currentCell[1]];
  }

  const nextRow = currentCell[1] + 1;
  if (nextRow >= rowCount) return null;

  return [0, nextRow];
};

export const getTabNavigationSelection = ({
  columnCount,
  rowCount,
  selection,
  trigger,
}: {
  columnCount: number;
  rowCount: number;
  selection: GridSelection;
  trigger: TabNavigationTrigger;
}): GridSelection | null => {
  const target = getTabNavigationTarget({
    columnCount,
    rowCount,
    selection,
    trigger,
  });
  if (!target) return null;

  return {
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
    current: {
      cell: target,
      range: {
        x: target[0],
        y: target[1],
        width: 1,
        height: 1,
      },
      rangeStack: [],
    },
  };
};
